/* eslint-disable @next/next/no-img-element */
'use client';
import { useRef, useState, useEffect, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';


const VALUES_DATA = [
  {
    id: 0,
    title: "Jyotishi Services",
    description: ["Seek guidance from verified Jyotishis.", "Receive clear, human responses based on authentic astrology.", "Choose text, audio, or live consultation as you prefer.", "Available in English, Hindi, Tamil, Telugu, Marathi and Kannada."],
    image: "/carousel/carousel-1.webp"
  },
  {
    id: 1,
    title: "Temple Poojas and Chadavs",
    description: ["Book a pooja for the deity of your choice, performed by trusted priests.", "Your name and family details are included in the sankalpa on your chosen day.", "The pooja is completed and video shared with you within a few hours."],
    image: "/carousel/carousel-2.webp"
  },
  {
    id: 2,
    title: "Kundali & Kundali Matching",
    description: ["Generate your birth chart instantly with verified astrologers.", "Check compatibility between two charts using traditional Jyotisha systems.", "Get detailed digital reports with clear results and explanations."],
    image: "/carousel/carousel-3.webp"
  },
  {
    id: 3,
    title: "Daily Essentials & More",
    description: ["Check Panchanga for the day and your location.", "Listen to studio-recorded Prarthanas for daily worship.", "Read your Rashifal for clear astrological insights on the day ahead.", "Explore many more features for everyday Hindu living."],
    image: "/carousel/carousel-4.webp"
  }
];

export default function MissionAndValues() {
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isInScrollZone, setIsInScrollZone] = useState(false);
  const scrollAccumulatorRef = useRef(0);
  const isScrollingRef = useRef(false);

  // Desktop wheel scroll handler - improved with proper scroll-out handling
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      // Only apply on desktop (md and above)
      if (typeof window === 'undefined' || window.innerWidth < 768) return;
      
      if (isScrollingRef.current) {
        e.preventDefault();
        return;
      }
      if (!cardRef.current) return;

      const cardRect = cardRef.current.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const cardCenter = cardRect.top + cardRect.height / 2;
      const distanceFromCenter = Math.abs(cardCenter - viewportCenter);
      
      // Check if card is in the center area of viewport
      const isCardInView = cardRect.top <= viewportCenter && cardRect.bottom >= viewportCenter;
      const isNearCenter = distanceFromCenter < 250;

      // Only intercept scroll when card is near viewport center AND we can change index
      if (isCardInView && isNearCenter) {
        const direction = e.deltaY > 0 ? 1 : -1;
        
        // Allow normal scroll when at boundaries
        if (activeIndex === 0 && direction < 0) {
          // At first item, scrolling up - allow normal scroll
          setIsInScrollZone(false);
          scrollAccumulatorRef.current = 0;
          return;
        }
        if (activeIndex === VALUES_DATA.length - 1 && direction > 0) {
          // At last item, scrolling down - allow normal scroll
          setIsInScrollZone(false);
          scrollAccumulatorRef.current = 0;
          return;
        }

        // Intercept scroll to change cards
        e.preventDefault();
        setIsInScrollZone(true);
        scrollAccumulatorRef.current += e.deltaY;
        
        const threshold = 50;

        if (Math.abs(scrollAccumulatorRef.current) > threshold) {
          isScrollingRef.current = true;

          setActiveIndex((prev) => {
            const newIndex = Math.max(0, Math.min(VALUES_DATA.length - 1, prev + direction));
            scrollAccumulatorRef.current = 0;
            
            setTimeout(() => {
              isScrollingRef.current = false;
            }, 500);

            return newIndex;
          });
        }
      } else {
        setIsInScrollZone(false);
        scrollAccumulatorRef.current = 0;
      }
    };
    
    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [activeIndex]);

  // Mobile scroll position tracking - based on container scroll position
  useEffect(() => {
    // Only apply on mobile
    if (typeof window === 'undefined' || window.innerWidth >= 768) return;
    if (!containerRef.current) return;
    
    const handleScroll = () => {
      if (!containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const triggerPoint = 50; // Start tracking when container top is 50px from viewport top
      
      // When container top reaches trigger point, start tracking
      if (containerRect.top <= triggerPoint) {
        // Calculate how much we've scrolled past the trigger point
        // When containerRect.top = 50, scrollPast = 0
        // When containerRect.top = 0, scrollPast = 50
        // When containerRect.top = -100, scrollPast = 150
        const scrollPastTop = triggerPoint - containerRect.top;
        
        // Each card transition happens over 60vh of scroll
        const scrollPerCard = viewportHeight * 0.6;
        const cardIndex = Math.floor(scrollPastTop / scrollPerCard);
        const clampedIndex = Math.min(VALUES_DATA.length - 1, Math.max(0, cardIndex));
        
        if (clampedIndex !== activeIndex) {
          setActiveIndex(clampedIndex);
        }
      } else if (containerRect.top > triggerPoint) {
        // Reset to first card when section is not yet in view
        if (activeIndex !== 0) {
          setActiveIndex(0);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeIndex]);

  return (
    <div
      ref={containerRef}
      className="bg-[#FDF8E9] relative"
      data-name="mission and values"
    >
      {/* Mobile View - Single card with scroll-triggered content updates */}
      <div className="md:hidden w-full relative pb-10" style={{ minHeight: `${VALUES_DATA.length * 60 + 60}vh` }}>
        {/* Sticky single card container - appears immediately */}
        <div className="sticky top-0 z-10 px-2 pt-4 pb-8">
          <div className="bg-white relative rounded-[16px] shadow-lg w-full p-6 min-h-[80vh] flex flex-col justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="w-full"
              >
                {/* Image at top for mobile */}
                <div className="relative w-full h-[450px] rounded-[12px] overflow-hidden mb-6 mt-2">
                  <img
                    alt={VALUES_DATA[activeIndex].title}
                    className="w-full h-full object-contain rounded-[12px]"
                    src={VALUES_DATA[activeIndex].image}
                  />
                </div>
                {/* List of all services accordion/expansion */}
                <div className="w-full mt-0 flex flex-col ">
                  {VALUES_DATA.map((service, idx) => (
                    <Fragment key={service.id}>
                      <div
                        className={`py-3 border-t border-[#EDE4DD] text-[16px] select-none flex items-center ${idx === activeIndex ? 'text-[#3c3838] font-semibold' : 'text-[#9b968f]'}`}
                        style={{transition: 'color 0.2s'}}
                      >
                        {service.title}
                      </div>
                      <AnimatePresence initial={false}>
                        {idx === activeIndex && (
                          <motion.ul
                            className="space-y-3 mb-2 pl-2"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            {service.description.map((item, dIdx) => (
                              <li key={dIdx} className="flex items-start gap-1 text-[14px] text-[#3c3838] font-medium">
                                <img src="/offerings-list.svg" alt="list-icon" className="w-[18px] h-[18px] mt-1 flex-shrink-0" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </Fragment>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Desktop View - Unchanged */}
      <div className="hidden md:flex flex-col items-center w-full">
        <div className="box-border content-stretch flex flex-col gap-[40px] md:gap-[60px] items-center px-4 md:px-8 lg:px-[120px] relative w-full max-w-[1440px]">
          {/* Values Card with Scroll Animation */}
          <div ref={cardRef} className="w-full max-w-[1040px] mx-auto min-h-[800px] flex items-center justify-center">
            <div className="bg-white relative rounded-[16px] shadow-lg w-full" data-name="Offerings">
              <div className="box-border content-stretch flex flex-col md:flex-row gap-[32px] items-start overflow-hidden p-[24px] md:p-[32px] relative rounded-[inherit] min-h-[600px] md:min-h-[744px]">
                {/* Left side - Values list */}
                <div className="basis-0 content-stretch flex flex-col gap-[20px] grow items-start min-h-px min-w-px relative shrink-0 w-full md:w-auto">
                  {VALUES_DATA.map((value, index) => (
                    <div key={value.id}>
                      {index > 0 && (
                        <div className="h-[1px] bg-[#ede4dd] w-full mb-[20px]" />
                      )}

                      <motion.div
                        initial={{ opacity: 0.5 }}
                        animate={{
                          opacity: activeIndex === index ? 1 : 0.5,
                        }}
                        transition={{ duration: 0.5 }}
                        className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full"
                      >
                        <motion.p
                          animate={{
                            color: activeIndex === index ? "#3c3838" : "#726e6e",
                          }}
                          transition={{ duration: 0.5 }}
                          className="font-['Noto_Sans:Display_SemiBold',sans-serif] font-semibold leading-[1.5] relative shrink-0 text-[20px] md:text-[24px]"
                          style={{ fontVariationSettings: "'CTGR' 100, 'wdth' 100" }}
                        >
                          {value.title}
                        </motion.p>

                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{
                            height: activeIndex === index ? "auto" : 0,
                            opacity: activeIndex === index ? 1 : 0,
                          }}
                          transition={{ duration: 0.5, ease: "easeInOut" }}
                          className="overflow-hidden mt-2" 
                        >
                          <ul className="space-y-3">
                            {value.description.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-[18px] md:text-[20px] text-[#3c3838] font-medium">
                                <img
                                  src="/offerings-list.svg"
                                  alt="list-icon"
                                  className="w-[18px] h-[18px] mt-1"
                                />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      </motion.div>
                    </div>
                  ))}
                </div>

                {/* Right side - Image */}
                <div className="relative w-full md:w-[410px] h-[400px] md:h-[680px] rounded-[16px] overflow-hidden shrink-0">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeIndex}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0"
                    >
                      <img
                        alt={VALUES_DATA[activeIndex].title}
                        className="w-full h-full object-cover rounded-[16px]"
                        src={VALUES_DATA[activeIndex].image}
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
              <div aria-hidden="true" className="absolute border border-[#ede4dd] border-solid inset-0 pointer-events-none rounded-[16px]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
